/**
 * newCopy entries for the AUTOMATION family
 */
import { FileResourceType } from '@dadohr/shared/domainTypes/experience';
import React from 'react';
import { AutomationIssueRaw, CopyPack, Issue, IssueKind } from '../types';
import {
  automationLink,
  ctxOf,
  emailFieldLabel,
  enrollmentFiltersLink,
  genericRetrySupportSteps,
  HELP_ARTICLES,
  HelpArticle,
  importOrEditProfileSteps,
  personLabel,
  retryStep,
  SupportEmail,
  tt,
  userProfileLink
} from './shared';

const rawOf = (issue: Issue) => issue.raw as AutomationIssueRaw;
// See legacyCopy/automation.tsx's identical comment: `AutomationActionDenormalized`
// is a 13-member union, one of which (Lessonly) lacks `errorData` entirely;
// neither cascade this taxonomy was extracted from ever handled
// Lessonly/Asana actions, so `any` here is exactly as safe as that.
const actionOf = (group: Issue[]): any => rawOf(group[0]).action;
const instanceOf = (group: Issue[]) => rawOf(group[0]).instance;
const errData = (group: Issue[]): any => actionOf(group).errorData;
const automationIdOf = (group: Issue[]): string | undefined => rawOf(group[0]).automationTplId;

/** "CODE: detail" → "CODE" (undefined when the message has no code prefix). */
const workdayErrorCode = (message: unknown): string | undefined =>
  typeof message === 'string' ? /^([A-Z0-9_]+)\b/.exec(message)?.[1] : undefined;

/** "CODE: detail" → "detail"; a bare code or non-string → ''. */
const workdayErrorDetail = (message: unknown): string => {
  if (typeof message !== 'string') return '';
  const i = message.indexOf(': ');
  return i >= 0 ? message.slice(i + 2) : '';
};

/** Links to the group's automation. A group whose occurrences come from more
 * than one automation (enrolment kinds group by target experience) renders
 * the plural label with no link, since no single automation is "the" one. */
const automationLinkFor = (group: Issue[], label = 'automation') => {
  const automationIds = new Set(group.map((issue) => rawOf(issue).automationTplId));
  if (automationIds.size > 1) return `${label}(s)`;
  return automationLink(ctxOf(group), automationIdOf(group), label);
};

/** Slack/Teams channel invites record the whole action actor as
 * `data.actionActor`; every other action records `data.actorKey`. */
const actorLabelFor = (group: Issue[]): string => {
  const d = errData(group)?.data;
  return personLabel(ctxOf(group), d?.actorKey ?? d?.actionActor?.key, instanceOf(group));
};

/** Which document platform a create-document action copies from. */
const documentProviderName = (group: Issue[]): string =>
  actionOf(group).srcTemplate?.type === FileResourceType.MS_ONE_DRIVE ? 'Microsoft' : 'Google';

const slackChannelName = (group: Issue[]): string => {
  const action = actionOf(group);
  const ed = errData(group);
  return (
    ed?.data?.channel?.name || (action.channel?.type === 'EXISTING' ? action.channel.name : '') || ''
  );
};

// The following named sections are shared only within this family file
// (every *_NO_ACTOR/*_NO_EMAIL/*_NO_MICROSOFT_USER kind across
// Google/Okta/MS/Teams/Slack) — kept local rather than in shared.tsx since
// no other file uses them. Every kind that uses one references it the same
// way, so editing one kind's copy for kind-specific reasons can't silently
// change another kind's text as a side effect.

/** "Nobody's assigned to this role" fix steps — identical across every
 * *_NO_ACTOR kind (Google/Okta/MS/Teams/Slack groups+channels, both
 * calendar-invite kinds). */
const noActor__howToFixIt = (ctx: ReturnType<typeof ctxOf>) => (
  <ul>
    <li>{tt(ctx, 'Click the participants listed below to open up each experience, and check that the role is now assigned')}</li>
    <li>
      {tt(ctx, 'If not, assign a user to this role')} <HelpArticle href={HELP_ARTICLES.changeStartDateOrManager} />
    </li>
    {retryStep()}
  </ul>
);

/** "No {{ email field }} on file" fix steps — shared by every *_NO_EMAIL
 * kind. `emailField` is the resolved "work email" / "personal email" label
 * (see `emailFieldOf`). */
const noEmail__howToFixIt = (ctx: ReturnType<typeof ctxOf>, emailField: string) => (
  <ul>
    <li>{tt(ctx, 'Click retry to see if their {{ emailField }} has since been added to Dado', { emailField })}</li>
    <li>
      {tt(ctx, 'If this does not work, add their {{ emailField }} by either:', { emailField })}
      <ul>{importOrEditProfileSteps(emailField)}</ul>
    </li>
    {retryStep()}
  </ul>
);

/** "Isn't in this Microsoft org/Team" fix steps — identical across all
 * three *_NO_MICROSOFT_USER / USER_NOT_IN_TEAM kinds except the first
 * bullet's location noun ("Microsoft organization" vs "Team"), exactly the
 * same shape as `noEmail__howToFixIt`'s substitutable noun. */
const noMicrosoftUser__howToFixIt = (ctx: ReturnType<typeof ctxOf>, emailAddress: string | undefined, orgLabel: string) => (
  <ul>
    <li>{tt(ctx, 'Click retry to see if they have since been added to this {{ orgLabel }}', { orgLabel })}</li>
    <li>
      {tt(
        ctx,
        'If this does not work, confirm their Microsoft email address matches what’s in Dado: {{ emailAddress }}. If you need to update the email in Dado, you can do so by either:',
        { emailAddress }
      )}
      <ul>{importOrEditProfileSteps('work email')}</ul>
    </li>
    {retryStep()}
  </ul>
);

/** "Nobody's assigned to this role" explanation — the sentence structure is
 * identical across every *_NO_ACTOR kind; only the trailing clause (which
 * service/what it couldn't do) varies. */
const noActor__whatCausedIt = (ctx: ReturnType<typeof ctxOf>, actor: string, trailingClause: string) => (
  <>
    {tt(ctx, 'When the automation(s) listed below ran, nobody was assigned as the')} <strong>{actor}</strong> {tt(ctx, trailingClause)}
  </>
);

/** Shared by the *_NO_EMAIL kinds for Google/Okta/Outlook groups and
 * Slack/Teams channels. The two calendar-invite NO_EMAIL kinds use
 * calendarNoEmail__whatCausedIt below instead. */
const noEmail__whatCausedIt = (
  ctx: ReturnType<typeof ctxOf>,
  fullName: string,
  emailField: string,
  serviceNoun: string
) => (
  <>
    {tt(ctx, 'When the automation(s) listed below ran, {{ fullName }} didn’t have a {{ emailField }} address in Dado.', {
      fullName,
      emailField
    })}{' '}
    {tt(ctx, 'Dado adds people to {{ serviceNoun }} using their email address, so without this address, no invites could be sent.', {
      serviceNoun
    })}
  </>
);

const calendarNoActor__summary = (g: Issue[]) =>
  tt(ctxOf(g), 'Calendar invite(s) couldn’t be sent: "{{ actor }}" not assigned', { actor: actorLabelFor(g) });

/** "work email" / "personal email", whichever the action was set up to use.
 * Group and calendar actions record `data.emailField`; Slack/Teams channel
 * invites record the whole action actor as `data.actionActor`. */
const emailFieldOf = (g: Issue[]): string => {
  const d = errData(g)?.data;
  return emailFieldLabel(d?.emailField ?? d?.actionActor?.emailField);
};

const calendarNoEmail__summary = (g: Issue[]) =>
  tt(ctxOf(g), 'Calendar invite(s) couldn’t be sent to {{ fullName }}: no {{ emailField }} in Dado', {
    fullName: actorLabelFor(g),
    emailField: emailFieldOf(g)
  });

const calendarNoEmail__whatCausedIt = (g: Issue[]) => {
  const c = ctxOf(g);
  const fullName = actorLabelFor(g);
  const emailField = emailFieldOf(g);
  return (
    <>{tt(c, 'When the automation(s) listed below ran, {{ fullName }} didn’t have a {{ emailField }} in Dado, so no calendar invites could be sent.', { fullName, emailField })}</>
  );
};

const noEventFound__copy = {
  summary: (g: Issue[]) =>
    tt(ctxOf(g), 'Calendar invite(s) couldn’t be sent: "{{ eventNameFromAction }}" event couldn’t be found', {
      eventNameFromAction: errData(g)?.data?.query || ''
    }),
  whatCausedIt: (g: Issue[]) => {
    const c = ctxOf(g);
    const eventNameFromAction = errData(g)?.data?.query || '';
    return (
      <>
        {tt(c, 'When the automation(s) listed below ran, Dado couldn’t find an event with a name like "{{ eventNameFromAction }}" scheduled in the future.', {
          eventNameFromAction
        })}{' '}
        {tt(c, 'Usually this means the event was renamed, deleted or the event series has ended.')}
      </>
    );
  },
  howToFixIt: (g: Issue[]) => {
    const c = ctxOf(g);
    return (
      <ul>
        <li>{tt(c, 'Check the event still exists in the same calendar, and there are future occurrences of this event. If it’s been deleted, restore the event.')}</li>
        <li>
          {tt(c, 'If the event has been renamed, update the name in the')} {automationLinkFor(g)}
        </li>
        <li>{tt(c, 'If the event still exists and has not been renamed, confirm that Dado can find the event')}</li>
        <li>
          {tt(c, 'After updating the calendar/event name in the')} {automationLinkFor(g)}
          {', '}
          {tt(c, 'come back here and click retry')}
        </li>
      </ul>
    );
  }
};

export const automationNewCopy = {
  // ---- ATLASSIAN_CREATE_ISSUE ----
  [IssueKind.ATLASSIAN_CREATE_ISSUE__INSUFFICIENT_PERMISSION]: {
    category: 'Jira ticket creation failed',
    summary: (g) => tt(ctxOf(g), 'Jira ticket(s) couldn’t be created: missing permissions'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When the automation(s) listed below ran, Dado didn’t have sufficient permissions to create the type of ticket in the project specified in the automation.'
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Confirm which project and ticket type are specified in the')} {automationLinkFor(g)}
          </li>
          <li>{tt(c, "Update the Dado ISU's permissions in Jira to ensure it is allowed to create that type of ticket in that project")}</li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__ACTOR_WITHOUT_EMAIL]: {
    category: 'Jira ticket creation failed',
    summary: (g) =>
      tt(ctxOf(g), "Jira ticket(s) couldn't be created: {{ fullName }} didn't have a work email in Dado", {
        fullName: errData(g).data?.actorName
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = errData(g).data?.actorName;
      return (
        <>
          {tt(c, 'When Dado tried to create this Jira ticket, {{ fullName }} didn’t have a work email in Dado.', { fullName })}{' '}
          {tt(
            c,
            "Dado identifies Jira accounts using work email. The automation is set up to include this person's details in the Jira ticket, so the ticket couldn't be created unless Dado finds their Jira account."
          )}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Click retry to see if their work email has since been added to Dado')}</li>
          <li>
            {tt(c, 'If this does not work, add a work email by either:')}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__API_ERROR]: {
    category: 'Jira ticket creation failed',
    summary: (g) => tt(ctxOf(g), 'Jira ticket(s) couldn’t be created: issues connecting to API'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When the automation(s) listed below ran, Dado had issues connecting to the Jira API. This is usually a passing issue, but can also be caused by problems with the Dado<>Jira integration.'
      ),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__DEFAULT]: {
    category: 'Jira ticket creation failed',
    summary: (g) => tt(ctxOf(g), 'Jira ticket(s) couldn’t be created: unknown issue'),
    whatCausedIt: (g) => tt(ctxOf(g), 'When the automation(s) listed below ran, an unknown issue prevented Dado from creating the Jira ticket.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__FIELDS_ERROR]: {
    category: 'Jira ticket creation failed',
    summary: (g) => {
      const c = ctxOf(g);
      const fieldError = (errData(g).data?.fieldErrors || [])[0];
      return tt(c, 'Jira ticket(s) couldn’t be created: issues with the "{{ fieldName }}" field', {
        fieldName: fieldError?.field || '—'
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fieldError = (errData(g).data?.fieldErrors || [])[0];
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, Jira rejected the value Dado provided for the')}{' '}
          <strong>{fieldError?.field || '—'}</strong> {tt(c, 'field. Jira provided the following details:')}{' '}
          <em>{fieldError?.message || '—'}</em>
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const fieldError = (errData(g).data?.fieldErrors || [])[0];
      const fieldName = fieldError?.field || '—';
      return (
        <ul>
          <li>
            {tt(c, 'Check the')} {automationLinkFor(g)} {tt(c, 'to see what values are being submitted for the {{ fieldName }} field', { fieldName })}
            <ul>
              <li>{tt(c, 'If the value sent by Dado is coming from the user data, you can check the user profile of each participant listed below to see the exact value')}</li>
            </ul>
          </li>
          <li>
            {tt(c, 'Review the requirements for the {{ fieldName }} field for this ticket type in Jira, and update the', { fieldName })}{' '}
            {automationLinkFor(g)} {tt(c, 'in Dado to match these requirements')}
          </li>
          <li>
            {tt(c, "If you can't spot the mismatch in requirements, reference our")}{' '}
            <HelpArticle href={'https://help.dadohr.com/article/72-jira-automation#troubleshooting'} />
          </li>
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(c, "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page.")}
          </li>
        </ul>
      );
    }
  },
  [IssueKind.ATLASSIAN_CREATE_ISSUE__NO_JIRA_USER]: {
    category: 'Jira ticket creation failed',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const fullName = ed.data?.actorName;
      return tt(c, "Jira ticket(s) couldn't be created: {{ fullName }} not a Jira user", { fullName });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const fullName = ed.data?.actorName;
      const workEmail = ed.data?.actorEmail || ed.data?.reporterEmail;
      return (
        <>
          {tt(c, "When Dado tried to create this Jira ticket, it couldn't find a Jira account with the email {{ workEmail }}.", { workEmail })}{' '}
          {tt(
            c,
            "This can sometimes happen if their work email address in Dado doesn't match their Jira account. The automation is set up to include this person's details in the Jira ticket, so the ticket couldn't be created unless Dado finds their Jira account."
          )}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const userId = ed.data?.userId;
      return (
        <ul>
          <li>
            {tt(c, "Confirm their work email on their")} {userProfileLink(c, userId, tt(c, 'user profile'))}{' '}
            {tt(c, "matches their Jira account. If you need to update the email, you can do so by either:")}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          <li>
            {tt(c, "Alternatively, if it's not necessary to include them in the Jira ticket, update the")}{' '}
            {automationLinkFor(g)} {tt(c, 'to remove the relevant fields')}
          </li>
          {retryStep()}
        </ul>
      );
    }
  },

  // ---- ENROLL_IN_EXPERIENCE ----
  [IssueKind.ENROLL_IN_EXPERIENCE__CRITERIA_NOT_MET]: {
    category: 'Enrollment into another experience failed',
    summary: (g) =>
      tt(ctxOf(g), 'Enrollment failed: participants didn’t match the filters for "{{ experienceName }}"', {
        experienceName: errData(g).data?.template?.name
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const experienceName = errData(g).data?.template?.name;
      return (
        <>
          {tt(c, 'The')} <strong>{experienceName}</strong>{' '}
          {tt(c, 'experience has filters that restrict who can be enrolled. When the automation(s) listed below ran, these participants did not match those filters, so their enrollment was rejected.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const target = errData(g).data?.template;
      return (
        <ul>
          <li>{tt(c, 'If it was right to prevent any of the participants listed below from being enrolled in this experience, click ignore')}</li>
          <li>
            {tt(c, 'If they should be enrolled, start by checking the')}{' '}
            {enrollmentFiltersLink(c, target?.id, tt(c, 'enrollment filters'))} {tt(c, 'set for')}{' '}
            <strong>{target?.name}</strong>.{' '}
            <HelpArticle href={'https://help.dadohr.com/article/8-experience-creator-participants#Defining-your-auto-enrollment-filters-tC1EO'} />{' '}
            {tt(c, 'Update these filters if they are incorrect; and/or update the user profile data for the participants listed below so it matches.')}
          </li>
          <li>
            {tt(c, 'If you need to update the data, you can do so by either:')}
            <ul>{importOrEditProfileSteps()}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__NO_ANCHOR]: {
    category: 'Enrollment into another experience failed',
    summary: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const anchorField = ed.data?.anchor?.field;
      const anchor =
        (c.customUserFields && c.getAnchorFields?.(c.customUserFields).find((x) => x.field === anchorField)?.shortLabel(c.t)) ||
        c.t('milestone');
      return tt(c, 'Enrollment failed: participants missing "{{ anchorDateField }}" field', { anchorDateField: anchor });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const experienceName = ed.data?.template?.name;
      const anchorField = ed.data?.anchor?.field;
      const anchor =
        (c.customUserFields && c.getAnchorFields?.(c.customUserFields).find((x) => x.field === anchorField)?.shortLabel(c.t)) ||
        c.t('milestone');
      return (
        <>
          {tt(c, 'The')} <strong>{experienceName}</strong> {tt(c, 'experience is anchored around a key date:')} <strong>{anchor}</strong>.{' '}
          {tt(
            c,
            "It's necessary for everyone enrolled in this experience to have this date set. When the automation(s) listed below ran, this date was not set on the user profile for the participants listed below, so their enrollment was rejected."
          )}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const ed = errData(g);
      const anchorField = ed.data?.anchor?.field;
      const anchor =
        (c.customUserFields && c.getAnchorFields?.(c.customUserFields).find((x) => x.field === anchorField)?.shortLabel(c.t)) ||
        c.t('milestone');
      return (
        <ul>
          <li>
            {tt(c, 'Check to see if the')} <strong>{anchor}</strong>{' '}
            {tt(c, 'field is now set for the participants listed below. If not, you can set it by either:')}
            <ul>{importOrEditProfileSteps(anchor)}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__EXPERIENCE_STOPPED]: {
    category: 'Enrollment into another experience failed',
    summary: (g) =>
      tt(ctxOf(g), 'Enrollment failed: "{{ experienceName }}" not active', { experienceName: errData(g).data?.template?.name }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const experienceName = errData(g).data?.template?.name;
      return (
        <>
          {tt(c, "People can't be enrolled into inactive experiences. When the automation(s) listed below ran,")}{' '}
          <strong>{experienceName}</strong> {tt(c, 'experience was inactive, so this enrollment was rejected.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Confirm that this is still the correct experience for these people to be enrolled into')}</li>
          <li>
            {tt(c, 'If not, update the')} {automationLinkFor(g)} {tt(c, 'to specify a different experience')}
          </li>
          <li>{tt(c, 'If this is still the right experience, ensure it is active (i.e. not stopped or still in draft)')}</li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__EXPERIENCE_ARCHIVED]: {
    category: 'Enrollment into another experience failed',
    summary: (g) =>
      tt(ctxOf(g), 'Enrollment failed: "{{ experienceName }}" is archived', { experienceName: errData(g).data?.template?.name }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const experienceName = errData(g).data?.template?.name;
      return (
        <>
          {tt(c, "People can't be enrolled into archived experiences. When the automation(s) listed below ran,")}{' '}
          <strong>{experienceName}</strong> {tt(c, 'experience was archived, so this enrollment was rejected.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Confirm that this is still the correct experience for these people to be enrolled into')}</li>
          <li>
            {tt(c, 'If not, update the')} {automationLinkFor(g)} {tt(c, 'to specify a different experience')}
          </li>
          <li>{tt(c, 'If this is still the right experience, unarchive it and start it')}</li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__ALREADY_ENROLLED]: {
    category: 'Enrollment into another experience failed',
    summary: (g) =>
      tt(ctxOf(g), 'Enrollment failed: participants already enrolled in "{{ experienceName }}"', {
        experienceName: errData(g).data?.template?.name
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const experienceName = errData(g).data?.template?.name;
      return (
        <>
          {tt(c, 'Each person can only have one active enrollment in each experience at a time. When the automation(s) listed below ran, these participants were already enrolled and active in')}{' '}
          <strong>{experienceName}</strong> {tt(c, 'experience, so a second enrollment was rejected.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const experienceName = errData(g).data?.template?.name;
      return (
        <ul>
          <li>
            {tt(c, 'Check to see if the participants listed below still have active experiences in')} <strong>{experienceName}</strong>
          </li>
          <li>{tt(c, 'If they do have an active experience, and you want to restart that experience for them, archive their current experience, and then come back here and click retry to re-enroll them.')}</li>
          <li>{tt(c, "If you don't want to restart their experience, click ignore")}</li>
        </ul>
      );
    }
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__TEMPLATE_NOT_FOUND]: {
    category: 'Enrollment into another experience failed',
    summary: (g) =>
      tt(ctxOf(g), 'Enrollment failed: "{{ experienceName }}" couldn’t be found', { experienceName: errData(g).data?.template?.name }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const experienceName = errData(g).data?.template?.name;
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, there was no experience in your Dado account called')}{' '}
          <strong>{experienceName}</strong>, {tt(c, 'so nobody could be enrolled in it.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Update the')} {automationLinkFor(g)} {tt(c, 'to specify the correct experience')}
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__TOO_FAR_IN_THE_PAST]: {
    category: 'Enrollment into another experience failed',
    summary: (g) => tt(ctxOf(g), 'Enrollment failed: experience would start too late'),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const experienceName = errData(g).data?.template?.name;
      return (
        <>
          {tt(c, 'The')} <strong>{experienceName}</strong>{' '}
          {tt(c, 'experience restricts late enrollments. When the automation(s) listed below ran, it would have enrolled the participants listed below too late, so the enrollment(s) were rejected.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const experienceName = errData(g).data?.template?.name;
      return (
        <ul>
          <li>{tt(c, 'If it was right to prevent any of the participants below from being enrolled in this experience, click ignore')}</li>
          <li>
            {tt(c, 'If not, you can amend the late enrollment rejection settings on')} <strong>{experienceName}</strong>{' '}
            <HelpArticle href={'https://help.dadohr.com/article/6-experience-creator-launch-date#Rejecting-late-enrollments-0h56z'} />{' '}
            {tt(c, 'and/or update the user profile data for the participants listed below so it matches.')}
          </li>
          <li>
            {tt(c, 'If you need to update the user profile data, you can do so by either:')}
            <ul>{importOrEditProfileSteps()}</ul>
          </li>
          {retryStep()}
          <li>
            {tt(c, 'Alternatively, enroll these participants manually in the experience')}{' '}
            <HelpArticle href={'https://help.dadohr.com/article/22-add-a-participant-to-a-running-cohort'} />{' '}
            {tt(c, 'When you enroll someone manually, you can skip and adjust the timing of items in their experience.')}
          </li>
        </ul>
      );
    }
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__ARCHIVE_FAILED]: {
    category: 'Re-enrollment failed',
    summary: (g) => tt(ctxOf(g), 'Re-enrollment failed: unknown issue'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), "An unknown issue prevented Dado archiving the participant's previous experience, which meant Dado could not enroll them again."),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.ENROLL_IN_EXPERIENCE__DEFAULT]: {
    category: 'Enrollment into another experience failed',
    summary: (g) => tt(ctxOf(g), 'Enrollment failed: unknown issue'),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const experienceName = errData(g).data?.template?.name;
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, an unknown issue prevented it from successfully enrolling the participant into the')}{' '}
          <strong>{experienceName}</strong> {tt(c, 'experience.')}
        </>
      );
    },
    howToFixIt: () => genericRetrySupportSteps()
  },

  // ---- GOOGLE_ADD_TO_GROUP ----
  [IssueKind.GOOGLE_ADD_TO_GROUP__NO_ACTOR]: {
    category: 'Google Groups invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Google groups invite(s) couldn’t be sent: "{{ actor }}" not assigned', { actor: actorLabelFor(g) }),
    whatCausedIt: (g) => noActor__whatCausedIt(ctxOf(g), actorLabelFor(g), "so Dado couldn't add them to any Google groups."),
    howToFixIt: (g) => noActor__howToFixIt(ctxOf(g))
  },
  [IssueKind.GOOGLE_ADD_TO_GROUP__NO_EMAIL]: {
    category: 'Google Groups invite failed',
    summary: (g) =>
      tt(ctxOf(g), "Google groups invite(s) couldn't be sent to {{ fullName }}: no {{ emailField }} in Dado", {
        fullName: actorLabelFor(g),
        emailField: emailFieldOf(g)
      }),
    whatCausedIt: (g) => noEmail__whatCausedIt(ctxOf(g), actorLabelFor(g), emailFieldOf(g), 'Google groups'),
    howToFixIt: (g) => noEmail__howToFixIt(ctxOf(g), emailFieldOf(g))
  },
  [IssueKind.GOOGLE_ADD_TO_GROUP__API_ERROR]: {
    category: 'Google Groups invite failed',
    summary: (g) => tt(ctxOf(g), 'Google groups invite(s) couldn’t be sent: issues connecting to API'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, Dado had issues connecting to the Google API. This is usually a passing issue, but can also be caused by problems with the Dado<>Google integration.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.GOOGLE_ADD_TO_GROUP__DEFAULT]: {
    category: 'Google Groups invite failed',
    summary: (g) => tt(ctxOf(g), 'Google groups invite(s) couldn’t be sent: unknown issue'),
    whatCausedIt: (g) => tt(ctxOf(g), 'When the automation(s) listed below ran, an unknown issue prevented the invitation from succeeding.'),
    howToFixIt: () => genericRetrySupportSteps()
  },

  // ---- OKTA_ADD_TO_GROUP ----
  [IssueKind.OKTA_ADD_TO_GROUP__NO_ACTOR]: {
    category: 'Okta Groups invite failed',
    summary: (g) => tt(ctxOf(g), 'Okta groups invite(s) couldn’t be sent: "{{ actor }}" not assigned', { actor: actorLabelFor(g) }),
    whatCausedIt: (g) => noActor__whatCausedIt(ctxOf(g), actorLabelFor(g), "so Dado couldn't add them to any Okta groups."),
    howToFixIt: (g) => noActor__howToFixIt(ctxOf(g))
  },
  [IssueKind.OKTA_ADD_TO_GROUP__NO_EMAIL]: {
    category: 'Okta Groups invite failed',
    summary: (g) =>
      tt(ctxOf(g), "Okta groups invite(s) couldn't be sent to {{ fullName }}: no {{ emailField }} in Dado", {
        fullName: actorLabelFor(g),
        emailField: emailFieldOf(g)
      }),
    whatCausedIt: (g) => noEmail__whatCausedIt(ctxOf(g), actorLabelFor(g), emailFieldOf(g), 'Okta groups'),
    howToFixIt: (g) => noEmail__howToFixIt(ctxOf(g), emailFieldOf(g))
  },
  [IssueKind.OKTA_ADD_TO_GROUP__NO_OKTA_USER]: {
    category: 'Okta Groups invite failed',
    summary: (g) => {
      const c = ctxOf(g);
      const fullName = actorLabelFor(g);
      return tt(c, 'Okta groups invite(s) couldn’t be sent to {{ fullName }}: couldn’t find their Okta account', { fullName });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = actorLabelFor(g);
      const emailAddress = errData(g).data?.emailAddress;
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, {{ fullName }} wasn’t a member of this Okta organization, so they couldn’t be added to group(s).', { fullName })}{' '}
          {tt(c, "This can sometimes happen if their work email address in Dado doesn't match their Okta account, or their Okta account is inactive.")}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const emailAddress = errData(g).data?.emailAddress;
      return (
        <ul>
          <li>{tt(c, 'Click retry to see if their Okta account has since been created')}</li>
          <li>
            {tt(c, 'If this does not work, confirm their Okta email address matches what’s in Dado: {{ emailAddress }}. If you need to update the email in Dado, you can do so by either:', { emailAddress })}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.OKTA_ADD_TO_GROUP__NO_GROUP]: {
    category: 'Okta Groups invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Okta groups invite(s) couldn’t be sent: "{{ groupName }}" group couldn’t be found', {
        groupName: errData(g)?.data?.group?.name || 'Unknown'
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const groupName = errData(g)?.data?.group?.name || 'Unknown';
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, Dado couldn’t find the')} <strong>{groupName}</strong>{' '}
          {tt(c, 'group. Usually this means the group was deleted or Dado doesn’t have the right permissions to add people to it.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const groupName = errData(g)?.data?.group?.name || 'Unknown';
      return (
        <ul>
          <li>
            {tt(c, 'Check the')} <strong>{groupName}</strong> {tt(c, 'group still exists')}
          </li>
          <li>
            {tt(c, 'If not, update the')} {automationLinkFor(g)} {tt(c, 'to specify a different group')}
          </li>
          <li>{tt(c, 'Confirm that Dado has the necessary permissions to invite people to this group')}</li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.OKTA_ADD_TO_GROUP__PERMISSION_DENIED]: {
    category: 'Okta Groups invite failed',
    summary: (g) => tt(ctxOf(g), 'Okta groups invite(s) couldn’t be sent: missing API permissions'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, Dado didn’t have the right permissions in the Okta API, so it couldn’t add people to the specified groups.'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Update Dado’s permissions in Okta')}</li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.OKTA_ADD_TO_GROUP__API_ERROR]: {
    category: 'Okta Groups invite failed',
    summary: (g) => tt(ctxOf(g), 'Okta groups invite(s) couldn’t be sent: issues connecting to API'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, Dado has issues connecting to the Okta API. This is usually a passing issue, but can also be caused by problems with the Dado<>Okta integration.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.OKTA_ADD_TO_GROUP__DEFAULT]: {
    category: 'Okta Groups invite failed',
    summary: (g) => tt(ctxOf(g), 'Okta groups invite(s) couldn’t be sent: unknown issue'),
    whatCausedIt: (g) => tt(ctxOf(g), 'When the automation(s) listed below ran, an unknown issue prevented the invitation from succeeding.'),
    howToFixIt: () => genericRetrySupportSteps()
  },

  // ---- GOOGLE_CALENDAR_EVENT_INVITATION ----
  [IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__API_ERROR]: {
    category: 'Calendar invite failed',
    summary: (g) => tt(ctxOf(g), 'Calendar invite(s) couldn’t be sent: issues connecting to Google Calendar'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, Dado had issues connecting to the Google calendar API. This is usually a passing issue, but can also be caused by problems with the Dado<>Google integration.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_ACTOR]: {
    category: 'Calendar invite failed',
    summary: calendarNoActor__summary,
    whatCausedIt: (g) =>
      noActor__whatCausedIt(ctxOf(g), actorLabelFor(g), "so Dado couldn't send a calendar invitation to them."),
    howToFixIt: (g) => noActor__howToFixIt(ctxOf(g))
  },
  [IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_EMAIL]: {
    category: 'Calendar invite failed',
    summary: calendarNoEmail__summary,
    whatCausedIt: calendarNoEmail__whatCausedIt,
    howToFixIt: (g) => noEmail__howToFixIt(ctxOf(g), emailFieldOf(g))
  },
  [IssueKind.GOOGLE_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND]: {
    category: 'Calendar invite failed',
    ...noEventFound__copy
  },

  // ---- MS_CALENDAR_EVENT_INVITATION ----
  [IssueKind.MS_CALENDAR_EVENT_INVITATION__API_ERROR]: {
    category: 'Calendar invite failed',
    summary: (g) => tt(ctxOf(g), 'Calendar invite(s) couldn’t be sent: issues connecting to Outlook'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, Dado had issues connecting to the Outlook calendar API. This is usually a passing issue, but can also be caused by problems with the Dado<>Microsoft integration.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_ACTOR]: {
    category: 'Calendar invite failed',
    summary: calendarNoActor__summary,
    whatCausedIt: (g) =>
      noActor__whatCausedIt(ctxOf(g), actorLabelFor(g), "so Dado couldn't send a calendar invitation to them."),
    howToFixIt: (g) => noActor__howToFixIt(ctxOf(g))
  },
  [IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_EMAIL]: {
    category: 'Calendar invite failed',
    summary: calendarNoEmail__summary,
    whatCausedIt: calendarNoEmail__whatCausedIt,
    howToFixIt: (g) => noEmail__howToFixIt(ctxOf(g), emailFieldOf(g))
  },
  [IssueKind.MS_CALENDAR_EVENT_INVITATION__NO_EVENT_FOUND]: {
    category: 'Calendar invite failed',
    ...noEventFound__copy
  },

  // ---- MS_ADD_TO_GROUP ----
  [IssueKind.MS_ADD_TO_GROUP__NO_ACTOR]: {
    category: 'Outlook Groups invite failed',
    summary: (g) => tt(ctxOf(g), 'Outlook groups invite(s) couldn’t be sent: "{{ actor }}" not assigned', { actor: actorLabelFor(g) }),
    whatCausedIt: (g) => noActor__whatCausedIt(ctxOf(g), actorLabelFor(g), "so Dado couldn't add them to any Outlook groups."),
    howToFixIt: (g) => noActor__howToFixIt(ctxOf(g))
  },
  [IssueKind.MS_ADD_TO_GROUP__NO_EMAIL]: {
    category: 'Outlook Groups invite failed',
    summary: (g) =>
      tt(ctxOf(g), "Outlook groups invite(s) couldn't be sent to {{ fullName }}: no {{ emailField }} in Dado", {
        fullName: actorLabelFor(g),
        emailField: emailFieldOf(g)
      }),
    whatCausedIt: (g) => noEmail__whatCausedIt(ctxOf(g), actorLabelFor(g), emailFieldOf(g), 'Outlook groups'),
    howToFixIt: (g) => noEmail__howToFixIt(ctxOf(g), emailFieldOf(g))
  },
  [IssueKind.MS_ADD_TO_GROUP__NO_MICROSOFT_USER]: {
    category: 'Outlook Groups invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Outlook groups invite(s) couldn’t be sent: {{ fullName }} not in this Microsoft organization', {
        fullName: actorLabelFor(g)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = actorLabelFor(g);
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, {{ fullName }} was not a member of this Microsoft organization, so couldn’t be added to group(s).', { fullName })}{' '}
          {tt(c, 'This can sometimes happen if their work email address in Dado doesn’t match their Microsoft email account, or their Microsoft account is inactive.')}
        </>
      );
    },
    howToFixIt: (g) => noMicrosoftUser__howToFixIt(ctxOf(g), errData(g)?.data?.email, 'Microsoft organization')
  },
  [IssueKind.MS_ADD_TO_GROUP__API_ERROR]: {
    category: 'Outlook Groups invite failed',
    summary: (g) => tt(ctxOf(g), 'Outlook groups invite(s) couldn’t be sent: issues connecting to API'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, Dado had issues connecting to the Microsoft API. This is usually a passing issue, but can also be caused by problems with the Dado<>Microsoft integration.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.MS_ADD_TO_GROUP__DEFAULT]: {
    category: 'Outlook Groups invite failed',
    summary: (g) => tt(ctxOf(g), 'Outlook groups invite(s) couldn’t be sent: unknown issue'),
    whatCausedIt: (g) => tt(ctxOf(g), 'When the automation(s) listed below ran, an unknown issue prevented the invitation from succeeding.'),
    howToFixIt: () => genericRetrySupportSteps()
  },

  // ---- MS_TEAMS_INVITE_TO_CHANNEL ----
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_ACTOR]: {
    category: 'Teams invite failed',
    summary: (g) => tt(ctxOf(g), 'Teams channel invite(s) couldn’t be sent: "{{ actor }}" not assigned', { actor: actorLabelFor(g) }),
    whatCausedIt: (g) => noActor__whatCausedIt(ctxOf(g), actorLabelFor(g), "so Dado couldn't add them to any Teams channels."),
    howToFixIt: (g) => noActor__howToFixIt(ctxOf(g))
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_EMAIL]: {
    category: 'Teams invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Teams channel invite(s) couldn’t be sent to {{ fullName }}: no {{ emailField }} in Dado', {
        fullName: actorLabelFor(g),
        emailField: emailFieldOf(g)
      }),
    whatCausedIt: (g) => noEmail__whatCausedIt(ctxOf(g), actorLabelFor(g), emailFieldOf(g), 'Teams channels'),
    howToFixIt: (g) => noEmail__howToFixIt(ctxOf(g), emailFieldOf(g))
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND]: {
    category: 'Teams invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Teams channel invite(s) couldn’t be sent: "{{ channelName }}" channel couldn’t be found', {
        channelName: slackChannelName(g)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const channelName = slackChannelName(g);
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, the')} <strong>{channelName}</strong>{' '}
          {tt(c, 'channel couldn’t be found. Usually this means the channel was deleted or Dado doesn’t have the right permissions to add people to it.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const channelName = slackChannelName(g);
      return (
        <ul>
          <li>
            {tt(c, 'Check the')} <strong>{channelName}</strong> {tt(c, 'channel still exists')}
          </li>
          <li>
            {tt(c, 'If not, update the')} {automationLinkFor(g)} {tt(c, 'to specify a different channel')}
          </li>
          <li>{tt(c, 'Confirm that Dado’s Teams app has the necessary permissions to invite people to this channel')}</li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__DEFAULT]: {
    category: 'Teams invite failed',
    summary: (g) => tt(ctxOf(g), 'Teams invite(s) couldn’t be sent: issues connecting to API'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, Dado had issues connecting to the Microsoft Teams API. This is usually a passing issue, but can also be caused by problems with the Dado<>Microsoft integration.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__NO_MICROSOFT_USER]: {
    category: 'Teams invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Teams channel invite(s) couldn’t be sent to {{ fullName }}: they’re not in this Microsoft organization', {
        fullName: actorLabelFor(g)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = actorLabelFor(g);
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, {{ fullName }} was not a member of this Microsoft organization, so couldn’t be added to Teams channels.', { fullName })}{' '}
          {tt(c, 'This can sometimes happen if their work email address in Dado doesn’t match their Microsoft email account, or their Microsoft account is inactive.')}
        </>
      );
    },
    howToFixIt: (g) => noMicrosoftUser__howToFixIt(ctxOf(g), errData(g)?.data?.email, 'Microsoft organization')
  },
  [IssueKind.MS_TEAMS_INVITE_TO_CHANNEL__USER_NOT_IN_TEAM]: {
    category: 'Teams invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Teams channel invite(s) couldn’t be sent to {{ fullName }}: they’re not in this Team', {
        fullName: actorLabelFor(g)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = actorLabelFor(g);
      const teamName = errData(g)?.data?.team?.name;
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, {{ fullName }} was not a member of the', { fullName })}{' '}
          <strong>{teamName}</strong> {tt(c, 'Team, so could not be added to any channels in this Team.')}{' '}
          {tt(c, 'This can sometimes happen if their work email address in Dado doesn’t match their Microsoft email account, or their Microsoft account is inactive.')}
        </>
      );
    },
    howToFixIt: (g) => noMicrosoftUser__howToFixIt(ctxOf(g), errData(g)?.data?.email, 'Team')
  },

  // ---- SLACK_INVITE_TO_CHANNEL ----
  [IssueKind.SLACK_INVITE_TO_CHANNEL__NO_EMAIL]: {
    category: 'Slack channel invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Slack channel invite(s) couldn’t be sent to {{ fullName }}: no {{ emailField }} in Dado', {
        fullName: actorLabelFor(g),
        emailField: emailFieldOf(g)
      }),
    whatCausedIt: (g) => noEmail__whatCausedIt(ctxOf(g), actorLabelFor(g), emailFieldOf(g), 'Slack channels'),
    howToFixIt: (g) => noEmail__howToFixIt(ctxOf(g), emailFieldOf(g))
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__NO_SLACK_USER]: {
    category: 'Slack channel invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Slack channel invite(s) couldn’t be sent to {{ fullName }}: couldn’t find their Slack account', {
        fullName: actorLabelFor(g)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = actorLabelFor(g);
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, Dado couldn’t find {{ fullName }}’s Slack account.', { fullName })}{' '}
          {tt(c, 'This can happen if their Slack Account is not active, they are not in the relevant Slack Workspace, or their work email address in Dado doesn’t match their Slack account.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const emailAddress = errData(g)?.data?.email;
      return (
        <ul>
          <li>{tt(c, 'Click retry to see if they now have an active Slack account in this workspace')}</li>
          <li>
            {tt(c, 'If this does not work, confirm their email address in Slack matches what’s in Dado: {{ emailAddress }}. If you need to update the email in Dado, you can do so by either:', { emailAddress })}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__NOT_IN_CHANNEL]: {
    category: 'Slack channel invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Slack channel invite(s) couldn’t be sent: Dado app not in {{ channelName }} channel', {
        channelName: slackChannelName(g)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const channelName = slackChannelName(g);
      return (
        <>
          {tt(c, 'For Dado to be able to add people to a channel, the Slack Dado app must be added to that channel. When the automation(s) listed below ran, the Dado app was not present in the')}{' '}
          <strong>{channelName}</strong> {tt(c, 'channel.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const channelName = slackChannelName(g);
      return (
        <ul>
          <li>
            {tt(c, 'Add the Dado app to')} <strong>{channelName}</strong> {tt(c, 'channel')}
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__NO_ACTOR]: {
    category: 'Slack channel invite failed',
    summary: (g) => tt(ctxOf(g), 'Slack channel invite(s) couldn’t be sent: "{{ actor }}" not assigned', { actor: actorLabelFor(g) }),
    whatCausedIt: (g) => noActor__whatCausedIt(ctxOf(g), actorLabelFor(g), "so Dado couldn't add them to any Slack channels."),
    howToFixIt: (g) => noActor__howToFixIt(ctxOf(g))
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__CHANNEL_NOT_FOUND]: {
    category: 'Slack channel invite failed',
    summary: (g) =>
      tt(ctxOf(g), 'Slack channel invite(s) couldn’t be sent: "{{ channelName }}" channel couldn’t be found', {
        channelName: slackChannelName(g)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const channelName = slackChannelName(g);
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, Dado could not find the')} <strong>{channelName}</strong>{' '}
          {tt(c, 'channel. Usually this means the channel was deleted or the Dado app is not present in that channel.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const channelName = slackChannelName(g);
      return (
        <ul>
          <li>
            {tt(c, 'Check the')} <strong>{channelName}</strong> {tt(c, 'channel still exists')}
          </li>
          <li>
            {tt(c, 'If not, contact')} <SupportEmail /> {tt(c, 'to ask for the Supporting Actor to be updated to a new channel')}
          </li>
          <li>
            {tt(c, 'Confirm the Dado app has been added to the')} <strong>{channelName}</strong> {tt(c, 'channel')}{' '}
            <HelpArticle href={HELP_ARTICLES.slackGrantBotAccess} />
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.SLACK_INVITE_TO_CHANNEL__DEFAULT]: {
    category: 'Slack channel invite failed',
    summary: (g) => tt(ctxOf(g), 'Slack channel invite(s) couldn’t be sent: issues connecting to API'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, Dado had issues connecting to the Slack API. This is usually a passing issue, but can also be caused by problems with the Dado<>Slack integration.'),
    howToFixIt: () => genericRetrySupportSteps()
  },

  // ---- CREATE_DOCUMENT_FROM_TEMPLATE ----
  [IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__TEMPLATE_NOT_FOUND]: {
    category: 'Document creation failed',
    summary: (g) => tt(ctxOf(g), 'Document couldn’t be created: template is missing'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When the automation(s) listed below ran, the template file Dado should be copying to create the documents couldn’t be found, so Dado couldn’t create any documents.'
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Check which template file is specified in the')} {automationLinkFor(g)}{' '}
            {tt(c, 'and confirm the file still exists.')}
          </li>
          <li>{tt(c, 'Sometimes moving this file to a different location can prevent Dado from accessing this. If you have moved the file, move it back.')}</li>
          {retryStep()}
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(c, "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page.")}
          </li>
        </ul>
      );
    }
  },
  [IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__TARGET_RESOURCE_MISSING]: {
    category: 'Document creation failed',
    summary: (g) => tt(ctxOf(g), 'Document couldn’t be created: folder is missing'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When the automation(s) listed below ran, the folder where Dado should put the newly-created documents couldn’t be found, so Dado couldn’t create any documents.'
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Check which folder is specified in the')} {automationLinkFor(g)}{' '}
            {tt(c, 'and confirm the folder still exists.')}
          </li>
          <li>{tt(c, 'Sometimes moving this folder to a different location can prevent Dado from accessing this. If you have moved the folder, move it back.')}</li>
          {retryStep()}
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(c, "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page.")}
          </li>
        </ul>
      );
    }
  },
  [IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__UNABLE_TO_CREATE_DOCUMENT]: {
    category: 'Document creation failed',
    summary: (g) => tt(ctxOf(g), 'Document couldn’t be created: Dado is missing permissions'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, Dado was missing some of the permissions required to be able to create documents.'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Confirm that Dado’s ISU has the right permissions to create documents.')}</li>
          {retryStep()}
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(c, "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page.")}
          </li>
        </ul>
      );
    }
  },
  [IssueKind.CREATE_DOCUMENT_FROM_TEMPLATE__API_ERROR]: {
    category: 'Document creation failed',
    summary: (g) => tt(ctxOf(g), 'Document couldn’t be created: issues connecting to API'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When the automation(s) listed below ran, Dado had issues connecting to the {{ provider }} API. This is usually a passing issue, but can also be caused by problems with the Dado<>{{ provider }} integration.',
        { provider: documentProviderName(g) }
      ),
    howToFixIt: () => genericRetrySupportSteps()
  },

  // ---- HTTP_POST ----
  [IssueKind.HTTP_POST__BAD_REQUEST]: {
    category: 'HTTP Post request failed',
    summary: (g) => tt(ctxOf(g), 'HTTP Post request failed: issues with the request'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), '400 error: the request Dado tried to send was rejected by the server because it looks malformed, corrupted, or incorrect.'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Wait a few minutes and click retry')}</li>
          <li>{tt(c, 'If this still doesn’t work, contact the person or team responsible for the API you want Dado to call, to confirm the correct format for the request')}</li>
        </ul>
      );
    }
  },
  [IssueKind.HTTP_POST__DEFAULT]: {
    category: 'HTTP Post request failed',
    summary: (g) => tt(ctxOf(g), 'HTTP Post request failed: unknown issue'),
    whatCausedIt: (g) => tt(ctxOf(g), 'When the automation(s) listed below ran, an unknown issue prevented the HTTP request from succeeding.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.HTTP_POST__NOT_ALLOWED]: {
    category: 'HTTP Post request failed',
    summary: (g) => tt(ctxOf(g), 'HTTP Post request failed: URL didn’t accept Post requests'),
    whatCausedIt: (g) => tt(ctxOf(g), '405 error: the URL provided in the automation doesn’t accept Post requests'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Contact the person or team responsible for the API you want Dado to call, to confirm the correct URL')}</li>
          <li>
            {tt(c, 'Update the URL in the')} {automationLinkFor(g)}
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.HTTP_POST__NOT_FOUND]: {
    category: 'HTTP Post request failed',
    summary: (g) => tt(ctxOf(g), 'HTTP Post request failed: incorrect URL'),
    whatCausedIt: (g) => tt(ctxOf(g), '404 error: the URL provided in the automation doesn’t exist'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Check that the URL in the')} {automationLinkFor(g)} {tt(c, 'is correct and update it if needed')}
          </li>
          {retryStep()}
          <li>{tt(c, 'If this still doesn’t work, contact the person or team responsible for the API you want Dado to call, to confirm the correct URL')}</li>
        </ul>
      );
    }
  },
  [IssueKind.HTTP_POST__TIMEOUT]: {
    category: 'HTTP Post request failed',
    summary: (g) => tt(ctxOf(g), 'HTTP Post request failed: request timed out'),
    whatCausedIt: (g) => tt(ctxOf(g), 'When Dado made a request to the specified URL, the request timed out'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Wait a few minutes and click retry')}</li>
          <li>{tt(c, 'If this still doesn’t work, contact the person or team responsible for the API you want Dado to call, to confirm you have the correct details for this Post request')}</li>
        </ul>
      );
    }
  },
  [IssueKind.HTTP_POST__UNAUTHORIZED]: {
    category: 'HTTP Post request failed',
    summary: (g) => tt(ctxOf(g), 'HTTP Post request failed: missing/incorrect authorization'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        '401 error: the server could not verify Dado’s identity because no authentication credentials were provided, or the authentication credentials set in the automation are incorrect, or expired'
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Check that the authentication settings in the')} {automationLinkFor(g)}{' '}
            {tt(c, 'are correct and update them if needed')}
          </li>
          {retryStep()}
          <li>{tt(c, 'If this still doesn’t work, contact the person or team responsible for the API you want Dado to call, to request new authentication credentials')}</li>
        </ul>
      );
    }
  },

  // ---- UNENROLL_FROM_EXPERIENCE ----
  [IssueKind.UNENROLL_FROM_EXPERIENCE]: {
    category: 'Unenrollment from this experience failed',
    summary: (g) => tt(ctxOf(g), 'Unenrollment failed: unknown issue'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, an unknown issue prevented it from successfully unenrolling the participant from this experience.'),
    howToFixIt: () => genericRetrySupportSteps()
  },

  // ---- SYNC_TO_DATA_DESTINATION ----
  [IssueKind.SYNC_TO_DATA_DESTINATION]: {
    category: 'Data sync failed',
    summary: (g) => tt(ctxOf(g), 'Data destination sync failed: unknown issue'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, an unknown issue prevented it from successfully syncing data to the selected Data Destination.'),
    howToFixIt: () => genericRetrySupportSteps()
  },

  // ---- WORKDAY_TRIGGER_BUSINESS_PROCESS ----
  // errorData.message is the worker's "<CODE>: <detail>"; the detail is shown,
  // the code picks the fix (same branches as the legacy copy).
  [IssueKind.WORKDAY_TRIGGER_BUSINESS_PROCESS]: {
    category: 'Workday update failed',
    summary: (g) => {
      const detail = workdayErrorDetail(errData(g)?.message);
      return detail
        ? tt(ctxOf(g), 'Workday update failed: {{ detail }}', { detail })
        : tt(ctxOf(g), 'Workday update failed: unknown issue');
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const detail = workdayErrorDetail(errData(g)?.message);
      return detail ? (
        <>
          {tt(c, 'When the automation(s) listed below ran, Workday couldn’t start the business process. Workday provided the following details:')}{' '}
          <em>{detail}</em>
        </>
      ) : (
        tt(c, 'When the automation(s) listed below ran, an unknown issue prevented the Workday business process from starting.')
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const code = workdayErrorCode(errData(g)?.message);
      if (code === 'WORKDAY_BP_EMPLOYEE_ONLY')
        return tt(c, 'This business process only applies to employees, so there’s nothing to fix for contingent workers. You can ignore this issue.');
      const step =
        code === 'WORKDAY_BP_REFERENCE_NOT_FOUND' || code === 'WORKDAY_BP_REFERENCE_AMBIGUOUS'
          ? tt(c, 'Set the participant’s mapped field to the Workday ID or the exact Workday name')
          : code === 'WORKDAY_BP_MISSING_VALUE' || code === 'WORKDAY_BP_INVALID_AMOUNT' || code === 'WORKDAY_BP_START_DATE_EMPTY'
            ? tt(c, 'Fill in the participant’s mapped field')
            : code === 'WORKDAY_BP_MISSING_SETTING'
              ? tt(c, 'Complete the business process settings in the automation')
              : code === 'WORKDAY_WORKER_ID_NOT_FOUND'
                ? tt(c, 'The participant has no Workday ID. Check the Workday import’s field mapping')
                : tt(c, 'Check the Workday integration connection and permissions');
      return (
        <ul>
          <li>{step}</li>
          {retryStep()}
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(c, "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page.")}
          </li>
        </ul>
      );
    }
  },

  // ---- DOCEBO_ENROLL ----
  [IssueKind.DOCEBO_ENROLL__NO_ACTOR]: {
    category: 'LMS enrollment failed',
    summary: (g) =>
      tt(ctxOf(g), 'Docebo enrollment failed: "{{ actor }}" not assigned', { actor: actorLabelFor(g) }),
    whatCausedIt: (g) =>
      noActor__whatCausedIt(ctxOf(g), actorLabelFor(g), "so Dado couldn't enroll them in the course."),
    howToFixIt: (g) => noActor__howToFixIt(ctxOf(g))
  },
  [IssueKind.DOCEBO_ENROLL__NO_EMAIL]: {
    category: 'LMS enrollment failed',
    summary: (g) =>
      tt(ctxOf(g), 'Docebo enrollment failed for {{ fullName }}: no {{ emailField }} in Dado', {
        fullName: actorLabelFor(g),
        emailField: emailFieldLabel(errData(g)?.data?.emailField)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = actorLabelFor(g);
      const emailField = emailFieldLabel(errData(g)?.data?.emailField);
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, {{ fullName }} didn’t have a {{ emailField }} in Dado.', { fullName, emailField })}{' '}
          {tt(c, 'Dado enrolls people in Docebo courses using their email address, so without this address, no enrollment could be sent.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const emailField = emailFieldLabel(errData(g)?.data?.emailField);
      return (
        <ul>
          <li>{tt(c, 'Click retry to see if their {{ emailField }} has since been added to Dado', { emailField })}</li>
          <li>
            {tt(c, 'If this does not work, add their {{ emailField }} by either:', { emailField })}
            <ul>{importOrEditProfileSteps(emailField)}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.DOCEBO_ENROLL__NO_USER]: {
    category: 'LMS enrollment failed',
    summary: (g) =>
      tt(ctxOf(g), 'Docebo enrollment failed for {{ fullName }}: couldn’t find their Docebo account', {
        fullName: actorLabelFor(g)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = actorLabelFor(g);
      return (
        <>
          {tt(c, 'When the automation(s) listed below ran, Dado couldn’t find a Docebo account for {{ fullName }}.', { fullName })}{' '}
          {tt(c, "This can sometimes happen if their email address in Dado doesn't match their Docebo account, or their Docebo account is inactive.")}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Confirm that they have an active account in Docebo. If not, set up their account.')}</li>
          <li>{tt(c, 'Click retry to see if they have since been added to Docebo.')}</li>
          <li>
            {tt(c, "If this does not work, confirm their email address matches what's in Docebo. If you need to update the email in Dado, you can do so by either:")}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.DOCEBO_ENROLL__DEFAULT]: {
    category: 'LMS enrollment failed',
    summary: (g) => tt(ctxOf(g), 'Docebo enrollment failed: unknown issue'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the automation(s) listed below ran, an unknown issue prevented the enrollment from succeeding.'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, "In Docebo, check that the course settings and Dado's service account settings permit Dado to enroll someone in this course.")}
          </li>
          <li>
            {tt(c, 'Wait a few minutes and click')} <strong>retry</strong>
          </li>
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(c, "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page.")}
          </li>
        </ul>
      );
    }
  }
} satisfies Partial<CopyPack>;
